/**
 * Formulário POR TIPO de unidade organizacional manual (#899, S-079).
 *
 * Espelha, no frontend, o módulo `services/api/app/services/org_unit_forms.py`
 * (`ATTRIBUTES_BY_TYPE`/`ID_MAX_LENGTH_BY_TYPE`): só os 9 tipos do catálogo CBC
 * que aceitam unidade manual aparecem aqui, cada um com seus próprios campos,
 * tamanhos e opções — não existe "formulário genérico" para todos os tipos.
 *
 * As opções de `subtype` (rótulo e ordem) reproduzem EXATAMENTE a tela real do
 * CBC, documentada na spec da task — não são um resumo nem uma tradução livre.
 */

export type OrgUnitFormFieldKey = "name" | "city" | "country" | "subtype";

export interface OrgUnitFormFieldOption {
  value: string;
  /** Texto em inglês, também usado como chave de tradução (`t(label, { defaultValue: label })`). */
  label: string;
}

export interface OrgUnitFormField {
  key: OrgUnitFormFieldKey;
  maxLength: number;
  /** Presente só no campo `subtype` — vira `<select>` em vez de `<input>`. */
  options?: OrgUnitFormFieldOption[];
}

export interface OrgUnitFormDefinition {
  /** Tamanho máximo do ID (a chave), específico do tipo. `0` = id não editável. */
  idMaxLength: number;
  /** Dica exibida sob o campo ID — hoje só o formato do id composto. */
  idHint?: string;
  /** `false` só para `company` — os outros 8 tipos sempre têm pai no cookbook. */
  hasParent: boolean;
  /** Muitos-para-muitos (Distribution Channel, Division): liga-se a várias Sales Organizations
   *  (``parent_ids``) em vez de um pai só. */
  multiParent?: boolean;
  fields: OrgUnitFormField[];
}

const STORAGE_LOCATION_SUBTYPES: OrgUnitFormFieldOption[] = [
  { value: "finished_goods", label: "Standard Storage Location for Finished Goods" },
  { value: "semi_finished_goods", label: "Standard Storage Location for Semi-Finished Goods" },
  { value: "raw_material", label: "Raw Material" },
  { value: "warehouse", label: "Warehouse" },
  { value: "kanban", label: "Kanban" },
  { value: "return", label: "Return" },
];

const SHIPPING_POINT_SUBTYPES: OrgUnitFormFieldOption[] = [
  { value: "standard_shipping_point", label: "Standard Shipping Point" },
  {
    value: "shipping_point_customer_consignment",
    label: "Shipping Point for Customer Consignment",
  },
  { value: "shipping_point_raw_materials", label: "Shipping Point for Raw Materials" },
];

const PURCHASING_ORGANIZATION_SUBTYPES: OrgUnitFormFieldOption[] = [
  { value: "plant_specific", label: "Plant-Specific" },
  { value: "cross_plant", label: "Cross-Plant" },
  { value: "cross_company_code", label: "Cross-Company-Code" },
];

/**
 * Os 9 tipos que aceitam unidade manual, na ordem da tabela da spec. Qualquer
 * outro `level_code` (sales_area, division, operating_concern, …) fica de fora
 * — não é um tipo qualquer que a extração trouxe, é o catálogo do CBC.
 */
export const ORG_UNIT_FORM_BY_TYPE: Record<string, OrgUnitFormDefinition> = {
  company: {
    idMaxLength: 6,
    hasParent: false,
    fields: [
      { key: "name", maxLength: 30 },
      { key: "country", maxLength: 2 },
    ],
  },
  company_code: {
    idMaxLength: 4,
    hasParent: true,
    fields: [
      { key: "name", maxLength: 25 },
      { key: "city", maxLength: 40 },
    ],
  },
  plant: {
    idMaxLength: 4,
    hasParent: true,
    fields: [
      { key: "name", maxLength: 30 },
      { key: "city", maxLength: 40 },
    ],
  },
  storage_location: {
    idMaxLength: 4,
    hasParent: true,
    fields: [
      { key: "name", maxLength: 16 },
      { key: "city", maxLength: 40 },
      { key: "subtype", maxLength: 0, options: STORAGE_LOCATION_SUBTYPES },
    ],
  },
  shipping_point: {
    idMaxLength: 4,
    hasParent: true,
    fields: [
      { key: "name", maxLength: 30 },
      { key: "subtype", maxLength: 0, options: SHIPPING_POINT_SUBTYPES },
    ],
  },
  sales_organization: {
    idMaxLength: 4,
    hasParent: true,
    fields: [{ key: "name", maxLength: 20 }],
  },
  distribution_channel: {
    idMaxLength: 2,
    hasParent: true,
    multiParent: true,
    fields: [{ key: "name", maxLength: 20 }],
  },
  division: {
    idMaxLength: 2,
    hasParent: true,
    multiParent: true,
    fields: [{ key: "name", maxLength: 20 }],
  },
  sales_office: {
    idMaxLength: 4,
    hasParent: true,
    fields: [
      { key: "name", maxLength: 20 },
      { key: "city", maxLength: 40 },
    ],
  },
  purchasing_organization: {
    idMaxLength: 4,
    hasParent: true,
    fields: [
      { key: "name", maxLength: 20 },
      { key: "subtype", maxLength: 0, options: PURCHASING_ORGANIZATION_SUBTYPES },
    ],
  },
};

/** Os 9 `level_code` que aceitam unidade manual — usado para restringir o
 *  seletor de nível de "Create unit" ao catálogo do CBC (#899). */
export const ORG_UNIT_TYPES: ReadonlySet<string> = new Set(Object.keys(ORG_UNIT_FORM_BY_TYPE));

/** Tipo fora dos 9: só nasce extraído, a criação de manual recusa esse tipo.
 *  Na extraída a rota só aceita o `id` (e só quando o tipo tem regra de id
 *  conhecida); `name` (máx 40) fica aqui só como forma do tipo, à vista e
 *  travado no diálogo. Espelha `OtherUnitAttributes` do backend. */
export const OTHER_UNIT_FORM: OrgUnitFormDefinition = {
  idMaxLength: 0,
  hasParent: false,
  fields: [{ key: "name", maxLength: 40 }],
};

/**
 * Tipos fora dos 9 com id editável — espelha `EXTRACTED_ID_MAX_LENGTH_BY_TYPE`
 * e `COMPOSITE_ID_PARTS_BY_TYPE` do backend (tamanho do campo-chave no SAP).
 * A Sales Area tem id composto: o formato exato é validado no backend.
 * Objetos montados uma vez só: o diálogo memoiza pelo formulário do tipo.
 */
const EXTRACTED_UNIT_FORM_BY_TYPE: Record<string, OrgUnitFormDefinition> = {
  operating_concern: { ...OTHER_UNIT_FORM, idMaxLength: 4 },
  controlling_area: { ...OTHER_UNIT_FORM, idMaxLength: 4 },
  credit_control_area: { ...OTHER_UNIT_FORM, idMaxLength: 4 },
  purchasing_group: { ...OTHER_UNIT_FORM, idMaxLength: 3 },
  sales_group: { ...OTHER_UNIT_FORM, idMaxLength: 3 },
  warehouse_number: { ...OTHER_UNIT_FORM, idMaxLength: 3 },
  sales_area: {
    ...OTHER_UNIT_FORM,
    idMaxLength: 10,
    idHint: "Format: sales organization|distribution channel|division, e.g. 3110|10|00.",
  },
};

export function orgUnitFormFor(levelCode: string): OrgUnitFormDefinition {
  return (
    ORG_UNIT_FORM_BY_TYPE[levelCode] ?? EXTRACTED_UNIT_FORM_BY_TYPE[levelCode] ?? OTHER_UNIT_FORM
  );
}

/**
 * Mapa achatado valor→rótulo de TODOS os subtypes dos 9 tipos, para exibição
 * fora do formulário (ex.: `OrgNodeDetailDrawer`). Os valores não colidem
 * entre tipos (cada subtype pertence a um único tipo no catálogo do CBC).
 */
export const SUBTYPE_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  Object.values(ORG_UNIT_FORM_BY_TYPE)
    .flatMap((definition) => definition.fields)
    .flatMap((field) => field.options ?? [])
    .map((option) => [option.value, option.label]),
);
